import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  shortcutService,
} from '../services/api'
import type {
  ShortcutPlace,
} from '../client'
import {
  IOS_ARRIVE_SHORTCUT_URL,
  IOS_LEAVE_SHORTCUT_URL,
  IOS_PLACE_AUTOMATION_URLS,
  IOS_SHORTCUT_URL,
  IOS_SIGNAL_SHORTCUT_URL,
} from '../config'

// The assistant's setup link adds `location=1`, and the launch announcement's
// button points at `#location-reminders`: either opens the location steps
// straight away. Scrolling to the card is the Settings page's section link.
const LOCATION_HASH = '#location-reminders'

function locationRequested(): boolean {
  return (
    new URLSearchParams(window.location.search).get('location') === '1'
    || window.location.hash === LOCATION_HASH
  )
}

// iPhone Action Button setup. Press the button, talk, done - the recording is
// transcribed and handed to the assistant on whichever channel the user's
// conversation already lives on, which is why the card names that channel
// rather than promising a reply here.
//
// Only the key is copied by hand: a Shortcut cannot sign in, and an iCloud
// link cannot carry a per-user parameter, so the install asks for it once.
// Binding the button itself is three taps in iOS Settings that no API can do
// for us, so the card just says which three.
export function ShortcutSection() {
  const [loaded, setLoaded] = useState(false)
  const [shortcutKey, setShortcutKey] = useState<string | null>(null)
  const [replyChannel, setReplyChannel] = useState<string | null>(null)
  // null while the location-reminders flag is off for this owner.
  const [places, setPlaces] = useState<ShortcutPlace[] | null>(null)
  // Captured at mount, like the Settings page's own deep links: its cleanup
  // effects strip the query string before the status fetch finishes.
  const [locationOpen, setLocationOpen] = useState(locationRequested)
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const [confirmingRotate, setConfirmingRotate] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const status = await shortcutService.getStatus()
      setShortcutKey(status.key)
      setReplyChannel(status.reply_channel)
      setPlaces(status.location_enabled ? (status.places ?? []) : null)
    } catch {
      // Unlike the add-on cards, this one is available to everybody - so a
      // transient failure has to say so. Vanishing would read as "my plan
      // doesn't have this", which is a support ticket, not a retry. The key
      // is a required field, so a missing one after load IS that failure.
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // The announcement banner is on the same page, so its link changes the hash
  // after this has mounted; listen rather than read once.
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === LOCATION_HASH) setLocationOpen(true)
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const handleCopy = async () => {
    if (!shortcutKey) return
    setError(null)
    try {
      await navigator.clipboard.writeText(shortcutKey)
      setCopied(true)
      // Long enough to read, short enough that the button is ready again by
      // the time they've switched to their phone and back.
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Couldn’t copy - select the key and copy it manually.')
    }
  }

  const handleRotate = async () => {
    setBusy(true)
    setError(null)
    try {
      setShortcutKey(await shortcutService.rotateKey())
      setConfirmingRotate(false)
    } catch {
      setError("Couldn't issue a new key - try again in a minute.")
    } finally {
      setBusy(false)
    }
  }

  if (!loaded) {
    return null
  }

  if (!shortcutKey) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-2">iPhone Action Button</h3>
        <p className="text-sm text-gray-700">
          Couldn&rsquo;t load your setup details just now.
        </p>
        <button
          onClick={() => void refresh()}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">iPhone Action Button</h3>
          <span className="inline-flex items-center rounded-full bg-gray-900 px-2 py-0.5 text-xs font-semibold tracking-wide text-white">
            NEW
          </span>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <p className="text-sm text-gray-700">
        Hold the Action Button, say what you need, let go. Your assistant picks
        it up and replies
        {replyChannel ? ` on ${replyChannel}` : ' on your usual channel'}.
      </p>

      {expanded ? (
        <div className="mt-4 space-y-4">
          <ol className="space-y-4 text-sm text-gray-700">
            <li>
              <span className="font-medium text-gray-900">1. Copy your key.</span>
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <code className="flex-1 break-all font-mono text-xs text-gray-600">
                  {shortcutKey}
                </code>
                <button
                  onClick={() => void handleCopy()}
                  className="text-sm font-medium text-gray-500 hover:text-gray-900"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </li>
            <li>
              <span className="font-medium text-gray-900">2. Add the shortcut.</span>{' '}
              {IOS_SHORTCUT_URL ? (
                <>
                  Open{' '}
                  <a
                    href={IOS_SHORTCUT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-gray-900"
                  >
                    this link
                  </a>{' '}
                  on your iPhone and paste the key when it asks.
                  <span className="mt-1 block text-gray-500">
                    If tapping Add Shortcut does nothing, open Settings, find
                    Shortcuts, turn on Private Sharing, then open the link again.
                  </span>
                </>
              ) : (
                'The shortcut link is coming shortly - check back here.'
              )}
            </li>
            <li>
              <span className="font-medium text-gray-900">3. Assign the button.</span>{' '}
              On your iPhone, go to Settings &rsaquo; Action Button, swipe to
              Shortcut, and pick it.
            </li>
          </ol>

          {confirmingRotate ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-sm text-gray-700 mb-3">
                A new key stops the shortcut working on every iPhone you&rsquo;ve
                already set up, until you paste the new one in.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => void handleRotate()}
                  disabled={busy}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {busy ? 'Issuing…' : 'Issue a new key'}
                </button>
                <button
                  onClick={() => setConfirmingRotate(false)}
                  disabled={busy}
                  className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingRotate(true)}
              className="text-sm font-medium text-gray-500 hover:text-red-600"
            >
              Issue a new key
            </button>
          )}
        </div>
      ) : (
        <button
          onClick={() => setExpanded(true)}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
        >
          Set it up
        </button>
      )}

      {places !== null && (
        <LocationReminders
          shortcutKey={shortcutKey}
          places={places}
          open={locationOpen}
          copied={copied}
          onCopy={() => void handleCopy()}
          onOpen={() => setLocationOpen(true)}
          onRefresh={() => void refresh()}
        />
      )}
    </div>
  )
}

// The fixed-place shortcuts the card can offer, in the order it lists them.
// Keyed like the ping they send, "<transition>:<place>", which is also the
// key in VITE_IOS_PLACE_AUTOMATION_URLS.
const COMMON_PLACE_SHORTCUTS: ReadonlyArray<{ key: string, name: string }> = [
  { key: 'arrive:home', name: 'Catch: I’m home' },
  { key: 'leave:home', name: 'Catch: I left home' },
  { key: 'arrive:office', name: 'Catch: I’m at the office' },
  { key: 'leave:office', name: 'Catch: I left the office' },
]

// A 20-second screen recording of the one-link install, shown beside step 1.
// Served from public/ like the lottie files; a new recording gets a new name
// so a cached copy never outlives the flow it shows.
const SETUP_VIDEO_URL = '/videos/location-setup-ios-v2.mp4'
const SETUP_VIDEO_POSTER_URL = '/videos/location-setup-ios-v2.jpg'

type Platform = 'ios' | 'android'

interface LocationRemindersProps {
  shortcutKey: string
  places: ShortcutPlace[]
  open: boolean
  copied: boolean
  onCopy: () => void
  onOpen: () => void
  onRefresh: () => void
}

// "Remind me when I get home." The phone's own geofence decides when; it only
// tells Catch a place name, so there is nothing to grant and nothing to track.
// iOS 27 or later only. A shared shortcut there carries its Arrive/Leave
// trigger, so a link plus an address is the whole setup. The card offers only
// the home and office links: the hand-built recipe for any other place (and
// for non-place signals) was dropped from it on 2026-10-26 as too much for the
// card, and lives in dev-docs/IOS_LOCATION_SHORTCUTS.md instead. Earlier iOS
// needed a hand-built automation with the request typed in; that path was
// retired on 2026-10-25 rather than kept beside this one. Android has no
// equivalent of a shareable automation yet, so its tab only says so.
function LocationReminders({
  shortcutKey, places, open, copied, onCopy, onOpen, onRefresh,
}: LocationRemindersProps) {
  const [platform, setPlatform] = useState<Platform>('ios')
  const automations = COMMON_PLACE_SHORTCUTS.filter((s) => IOS_PLACE_AUTOMATION_URLS[s.key])
  let step = 0
  const next = () => {
    step += 1
    return `${step}.`
  }
  const keyBox = (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <code className="flex-1 break-all font-mono text-xs text-gray-600">{shortcutKey}</code>
        <button onClick={onCopy} className="text-sm font-medium text-gray-500 hover:text-gray-900">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <span className="mt-1 block text-gray-500">
        Add Shortcut does nothing? Open Settings &rsaquo; Shortcuts, turn on
        Private Sharing, then open the link again.
      </span>
    </>
  )
  const tab = (value: Platform, label: string, badge?: string) => {
    const selected = platform === value
    return (
      <button
        type="button"
        aria-pressed={selected}
        onClick={() => setPlatform(value)}
        className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
          selected ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900'
        }`}
      >
        {label}
        {badge && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {badge}
          </span>
        )}
      </button>
    )
  }

  return (
    <div id="location-reminders" className="mt-6 scroll-mt-6 border-t border-gray-200 pt-5" data-testid="location-reminders">
      <h4 className="text-sm font-semibold text-gray-900">Location reminders</h4>
      <p className="mt-1 text-sm text-gray-700">
        &ldquo;Remind me when I get home.&rdquo; Your phone tells your assistant
        when you arrive at or leave a place; it sends the place&rsquo;s name,
        never your location. Needs iOS 27 or later.
      </p>
      <p className="mt-2 text-sm text-gray-500">
        {places.length > 0
          ? `Places set up so far: ${places.map((p) => `${p.place} (${p.transitions.join(', ')})`).join(', ')}.`
          : 'No places set up yet.'}
      </p>

      <div className="mt-4 flex gap-5 border-b border-gray-200" data-testid="location-platforms">
        {tab('ios', 'iPhone')}
        {tab('android', 'Android', 'Soon')}
      </div>

      {platform === 'android' ? (
        <p className="mt-4 text-sm text-gray-700" data-testid="location-android">
          Android is in the works. For now, location reminders need an iPhone on
          iOS 27 or later.
        </p>
      ) : (
        <>
          {open ? (
          <ol className="mt-4 space-y-4 text-sm text-gray-700" data-testid="location-steps">
            {automations.length === 0 ? (
              <li>The shortcuts are coming shortly; check back here.</li>
            ) : (
              <>
                <li className="sm:flex sm:items-start sm:gap-4" data-testid="location-automations">
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-gray-900">{next()} Home or office: one link.</span>{' '}
                    On your iPhone, open the one you want:
                    <ul className="mt-2 space-y-1">
                      {automations.map((s) => (
                        <li key={s.key}>
                          <a href={IOS_PLACE_AUTOMATION_URLS[s.key]} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">
                            {s.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                    <span className="mt-2 block">
                      Paste your key, pick the address, leave the automation on,
                      tap Add Shortcut.
                    </span>
                    {keyBox}
                    {/* Everything the card no longer spells out: the assistant's
                        setup link lands here for any place, so the owner must
                        find a next step and not a dead end. The recipe itself is
                        in the owner-reminders skill, so the assistant can give it. */}
                    {IOS_ARRIVE_SHORTCUT_URL && IOS_LEAVE_SHORTCUT_URL && (
                      <span className="mt-2 block text-gray-500" data-testid="location-other-places">
                        Another place, or a trigger like your car or a charger? Add{' '}
                        <a href={IOS_ARRIVE_SHORTCUT_URL} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">Catch: I arrived</a>,{' '}
                        <a href={IOS_LEAVE_SHORTCUT_URL} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">Catch: I left</a>
                        {IOS_SIGNAL_SHORTCUT_URL && (
                          <>
                            {' '}or{' '}
                            <a href={IOS_SIGNAL_SHORTCUT_URL} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">Catch: signal</a>
                          </>
                        )}
                        , then ask your assistant to walk you through the automation.
                      </span>
                    )}
                  </div>
                  <video
                    src={SETUP_VIDEO_URL}
                    poster={SETUP_VIDEO_POSTER_URL}
                    controls
                    muted
                    playsInline
                    preload="none"
                    aria-label="Setting up Catch: I’m home on an iPhone"
                    className="mt-3 w-44 shrink-0 rounded-xl border border-gray-200 sm:mt-0"
                    data-testid="location-setup-video"
                  />
                </li>
                <li>
                  <span className="font-medium text-gray-900">{next()} Run it once</span>, then tap{' '}
                  <button onClick={onRefresh} className="underline hover:text-gray-900">
                    Check again
                  </button>
                  ; the place appears above.
                  <span className="mt-1 block text-gray-500">
                    A test counts as a real arrival, so a reminder waiting on this
                    place goes out now; say it was a test and it&rsquo;ll be set again.
                  </span>
                </li>
              </>
            )}
          </ol>
          ) : (
            <button
              onClick={onOpen}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
            >
              Set up a place
            </button>
          )}

        </>
      )}
    </div>
  )
}
